/* 100X Equine — bundle builder -> Shopify cart handoff.
 *
 * WHAT THIS REPLACES. bundles-app.js computed the cheapest cover of a cart and then did
 * `location.href = 'Checkout.html'`, a static file left over from the design mockup. The builder
 * kept its cart in localStorage and never touched Shopify, so /pages/bundles and the homepage
 * builder could not complete a purchase at all.
 *
 * THE CONTRACT (100X_Equine_Shopify_to_Recharge_Handoff_SPEC): the storefront engine decides the
 * optimal package and hands checkout a list of REAL priced variants. Recharge then re-bills those
 * exact line items natively. Pricing must never live in a checkout discount, because a
 * first-order-only discount does not recur and the renewal would bill the undiscounted price.
 *
 * So every price the customer was quoted has to map to a real variant or combo SKU. That is what
 * the 3-unit tier SKUs are for, and why a quantity that has no SKU is a correctness problem
 * rather than a cosmetic one — see the reconciliation check below.
 */
(function () {
  'use strict';

  /* The builder's table when a builder is on the page, otherwise one derived from GX_SKU_MAP.
     Resolved on every call rather than cached into the global, so load order cannot decide which
     source wins. See dataOrMap() below for why that matters. */
  function data() { return dataOrMap(); }
  function map() { return window.GX_SKU_MAP || null; }

  /* Cheapest cover of the cart, returning WHICH bundles and singles were chosen.
     Same algorithm and same data as bundles-app.js's bestPrice(), which returns only a total.
     Memoised on the sorted entry list exactly as the original is. */
  function bestCover(cart, memo) {
    var d = data();
    memo = memo || {};
    var entries = Object.keys(cart).filter(function (k) { return cart[k] > 0; }).sort()
      .map(function (k) { return [k, cart[k]]; });
    if (!entries.length) return { cost: 0, bundles: [], singles: {} };

    var key = JSON.stringify(entries);
    if (key in memo) return memo[key];

    var singles = {};
    var baseline = 0;
    entries.forEach(function (e) {
      singles[e[0]] = e[1];
      baseline += d.productPrice(d.byKey[e[0]], e[1]);
    });
    var best = { cost: baseline, bundles: [], singles: singles };

    d.bundles.forEach(function (b) {
      var fits = Object.keys(b.items).every(function (k) { return (cart[k] || 0) >= b.items[k]; });
      if (!fits) return;
      var next = {};
      Object.keys(cart).forEach(function (k) { next[k] = cart[k]; });
      Object.keys(b.items).forEach(function (k) { next[k] -= b.items[k]; });
      var rest = bestCover(next, memo);
      var cost = b.price + rest.cost;
      if (cost < best.cost - 0.0001) {
        best = { cost: cost, bundles: [b.sku].concat(rest.bundles), singles: rest.singles };
      }
    });

    memo[key] = best;
    return best;
  }

  /* Split a quantity into tier SKUs that actually exist, at the CHEAPEST total price.
     Returns null if it cannot be composed at all.

     THIS WAS GREEDY (largest tier first) AND THAT WAS A BUG. Doug caught it 2026-08-06: 8 gallons
     stopped working the moment the 6-gallon SKU was created. Greedy took 6+2 = $816.40 where 4+4 =
     $805.12 matches the quote exactly, so the drift guard refused a quantity that had worked for
     months. Adding a tier must never break a quantity that already composed.

     Largest-first is only optimal when bigger tiers are always cheaper per unit. They are not:
     these price curves flatten at the top tier, so two 4s beat a 6 plus a 2. This walks every
     combination by cost instead, so a new tier can only ever help.

     Cost comes from `price` on each tier in GX_SKU_MAP. If a tier has no price the entry is
     skipped for costing and falls back to unit count, which degrades to the old behaviour rather
     than crashing. */
  function splitQty(productKey, qty) {
    var m = map();
    var p = m && m.products && m.products[productKey];
    if (!p) return null;

    var sizes = Object.keys(p.tiers)
      .filter(function (k) { return k !== '_' && p.tiers[k] && p.tiers[k].id; })
      .map(Number)
      .sort(function (a, b) { return b - a; });
    if (!sizes.length) return null;

    function priceOf(s) {
      var t = p.tiers[String(s)];
      var v = t && Number(t.price);
      return isFinite(v) && v > 0 ? v : s; // no price -> cost by unit count
    }

    /* best[n] = cheapest way to make exactly n units from WHOLE TIERS only */
    var best = [{ cost: 0, parts: [] }];
    for (var n = 1; n <= qty; n++) {
      var win = null;
      for (var i = 0; i < sizes.length; i++) {
        var s = sizes[i];
        if (s > n) continue;
        var prev = best[n - s];
        if (!prev) continue;
        var c = prev.cost + priceOf(s);
        if (!win || c < win.cost - 1e-9) win = { cost: c, parts: prev.parts.concat(s) };
      }
      best[n] = win;
    }
    var candidate = best[qty] ? { cost: best[qty].cost, parts: best[qty].parts, addons: 0 } : null;

    /* TIER RATE PATH. Doug 2026-08-06: "it should be tiered based" — once the customer reaches a
       tier, every further unit is at that tier's per-unit rate.

       Whole tiers alone can never express that. Past 4 gallons the curve is flat (Gut X $100.64
       a gallon), but the only ODD tiers are 1 and 3, both priced on the steeper under-4 curve.
       That is why 5, 7, 9 and 11 stayed blocked even after the 3- and 6-gallon SKUs existed, and
       why chasing it with one SKU per quantity never ends.

       So: for every tier T that fits, try [T] plus (qty - T) add-ons and keep the cheapest. The
       add-on is deliberately NOT in `tiers`, so it can never be chosen below the tier threshold;
       one gallon on its own must still cost the 1-gallon price, not the tier rate. */
    if (p.addon && p.addon.id && Number(p.addon.price) > 0) {
      var ap = Number(p.addon.price);

      /* ONLY TIERS ALREADY AT THE FLAT RATE MAY BE EXTENDED. This is the whole rule and getting
         it wrong UNDERCHARGES.

         First version let the add-on extend ANY tier, so Gut X 3 composed as the 2-gallon plus one
         add-on: $212.56 + $100.64 = $313.20 against a $318.84 three-gallon price. It shipped 3, 5
         and 6 gallons under price on four products before a simulation caught it.

         Tiers below the flat point are priced on the steeper introductory curve and must be sold
         whole. A tier is "at rate" when its per-unit price equals the add-on price, which is
         derived here rather than hardcoded: Gut X and friends flatten at 4 gallons, Hydra-MAX at
         2 containers. Half a cent of slack covers the fractional rates on Osteo-MAX and
         Amino-MAX. */
      var atRate = sizes.filter(function (s) {
        return Math.abs(priceOf(s) / s - ap) < 0.005;
      });

      atRate.forEach(function (s) {
        if (s > qty) return;
        var c = priceOf(s) + ap * (qty - s);
        if (!candidate || c < candidate.cost - 1e-9) {
          candidate = { cost: c, parts: [s], addons: qty - s };
        }
      });
    }
    if (!candidate) return null;
    // `cost` added 2026-08-10 so the engine can price a product without the React builder.
    // Purely additive: every existing caller reads only parts and addons.
    return { parts: candidate.parts, addons: candidate.addons, cost: candidate.cost };
  }

  /* ---------- standalone pricing data ----------
   *
   * bestCover() needs GX_BUNDLE_DATA: a per-product price function and the bundle table. Until
   * now that only ever came from `bundles-app.js` or `pdp-bundle-module.js`, both of which are
   * React modules. So any surface wanting to price a cart had to load React to reach a lookup
   * table, which rules out the cart drawer, since that ships on every page.
   *
   * This builds the same thing from GX_SKU_MAP, which is plain Liquid-rendered JSON and already
   * carries tier prices, and now carries combo contents and prices too.
   *
   * NOT A SECOND PRICING IMPLEMENTATION. productPrice defers to splitQty, the same function the
   * checkout path uses, so the composition and its cost come from one place. A duplicate ladder
   * is exactly how the PDP and bundle prices drifted apart before.
   *
   * Guarded with ||, so where the builder IS loaded its copy still wins and nothing about the
   * bundles page or home changes. */
  function dataFromMap() {
    var m = map();
    if (!m || !m.products) return null;

    var byKey = {};
    Object.keys(m.products).forEach(function (k) {
      if (k !== '_') byKey[k] = { key: k };
    });

    var bundles = [];
    Object.keys(m.combos || {}).forEach(function (sku) {
      var c = m.combos[sku];
      if (!c || !c.items || !(Number(c.price) > 0)) return;
      bundles.push({ sku: sku, items: c.items, price: Number(c.price) });
    });

    return {
      byKey: byKey,
      bundles: bundles,
      sub: 0.20,
      productPrice: function (entry, qty) {
        if (!entry || !qty) return 0;
        var r = splitQty(entry.key, qty);
        // Unavailable at any composition: price it out of contention rather than as free.
        return r && isFinite(r.cost) ? r.cost : Infinity;
      }
    };
  }

  /* DELIBERATELY NOT ASSIGNED TO THE GLOBAL AT LOAD TIME.
   *
   * First version did `window.GX_BUNDLE_DATA = window.GX_BUNDLE_DATA || dataFromMap()` here. That
   * is load-order dependent and it would have changed the bundles page. On /pages/shop this file
   * is deferred alongside pdp-bundle-module.js, whose own assignment is `|| {...}`. If this one
   * ran first, the map-derived data would have won and pdp's would have been skipped, silently
   * swapping the pricing source out from under a page that already worked.
   *
   * `data()` falls back lazily instead, so wherever a real builder is present its table is used,
   * every time, regardless of which script parsed first. The map is only ever a fallback for
   * surfaces that have no builder at all, which is the cart drawer. */
  var mapData = null;
  function dataOrMap() {
    if (window.GX_BUNDLE_DATA) return window.GX_BUNDLE_DATA;
    if (!mapData) mapData = dataFromMap();
    return mapData;
  }

  /* Callers outside this file must ask through here, NEVER by reading window.GX_BUNDLE_DATA.
     That global is only ever set by the React builders, so on a product page it does not exist
     and a consumer testing for it would conclude the engine is unavailable. The cart drawer did
     exactly that and silently lost the feature on every page without a builder. */
  window.GXBundleData = dataOrMap;

  function planFor(entry, cadence) {
    if (!entry || !entry.plans) return null;
    return entry.plans[String(cadence)] || entry.plans['90'] || null;
  }

  /* Build Shopify line items from the chosen cover. */
  function buildLines(cart, mode, cadence) {
    var m = map(), d = data();
    if (!m || !d) return { error: 'map-or-data-missing' };

    var cover = bestCover(cart);
    var lines = [], unresolved = [], composed = 0;

    cover.bundles.forEach(function (sku) {
      var c = m.combos && m.combos[sku];
      if (!c || !c.id) { unresolved.push('combo:' + sku); return; }
      var line = { id: c.id, quantity: 1 };
      if (mode === 'sub') {
        var pl = planFor(c, cadence);
        if (pl) line.selling_plan = pl; else unresolved.push('plan:' + sku);
      }
      lines.push(line);
    });

    Object.keys(cover.singles).forEach(function (k) {
      var qty = cover.singles[k];
      if (!qty) return;
      var split = splitQty(k, qty);
      if (!split) { unresolved.push('tier:' + k + 'x' + qty); return; }

      var entry = m.products[k];
      var plan = mode === 'sub' ? planFor(entry, cadence) : null;
      if (mode === 'sub' && !plan) unresolved.push('plan:' + k);

      /* TOTAL SHOWN TO THE CUSTOMER. Doug 2026-08-06, looking at a checkout that read
         "Gut X / 4 Gallons / x2": "is there anyway to display the total number to the customer
         and hand off what Recharge needs".

         A quantity is composed from whole tier SKUs, so checkout shows the PARTS, not the total:
         8 gallons is 4 Gallons x2, and 5 gallons is a 4-gallon plus an add-on. Correct cart,
         confusing label.

         A line item property is the only lever that reaches Shopify's own checkout without
         deploying a checkout UI extension. It renders under the line at checkout, on the order,
         and on the packing slip, which is useful for the warehouse too. The variant id and
         selling plan are untouched, so what Recharge re-bills does not change at all.

         SAME PROPERTY ON EVERY LINE, DELIBERATELY. Shopify treats properties as part of a line's
         identity: two GX400 lines with the SAME property still merge into one line at quantity 2,
         which is what produced the clean "x2" in the first place. Give them different values and
         they split into two lines and look like a double-charge.

         ONLY WHEN THE ORDER SPANS MORE THAN ONE LINE. Doug 2026-08-06: "1-6 gallons does not need
         the additional Your order: 6 Gallons Total on it."

         A quantity with its own SKU renders as a single line already saying the right thing:
         6 gallons is GX600, so checkout shows badge 1 and "6 Gallons" and the property just
         repeats it. Quantities 1, 2, 3, 4 and 6 are all exact tiers. 5 is not (4 + one add-on),
         so it still needs the label, which is why this keys off LINE COUNT and not a list of
         numbers: it stays correct automatically as tier SKUs are added or removed. */
      var UNIT = { 'hydra-max': 'Containers' };   // everything else ships in gallons
      var lineCount = split.parts.length + (split.addons > 0 ? 1 : 0);
      var props = lineCount > 1
        ? { 'Your order': qty + ' ' + (UNIT[k] || 'Gallons') + ' total' }
        : null;

      split.parts.forEach(function (size) {
        var t = entry.tiers[String(size)];
        var line = { id: t.id, quantity: 1 };
        if (props) line.properties = props;   // omit the key entirely when there is nothing to say
        if (plan) line.selling_plan = plan;
        lines.push(line);
        composed += d.productPrice(d.byKey[k], size);
      });

      /* Add-ons ride as ONE line with a quantity, not N separate lines. Eight gallons is a
         4-gallon plus "additional gallon x4", which is both readable on the order and what
         Recharge re-bills. Emitting four identical lines would look like four mistakes.

         Costed from the map's own add-on price rather than d.productPrice, because the engine's
         per-unit curve is what we are deliberately NOT using here: the whole point is the flat
         tier rate. */
      if (split.addons > 0) {
        var a = entry.addon;
        if (!a || !a.id) { unresolved.push('addon:' + k); return; }
        var aline = { id: a.id, quantity: split.addons };
        if (props) aline.properties = props;
        if (plan) aline.selling_plan = plan;
        lines.push(aline);
        composed += Number(a.price) * split.addons;
      }
    });

    /* RECONCILIATION. The engine quoted a price using the half-tier rule; the cart can only
       contain SKUs that exist. If a quantity had to be split into smaller tiers because its own
       SKU is missing, the customer would be charged MORE than they were quoted. Surfaced rather
       than silently overcharging — the fix is to create the missing tier SKU. */
    var quoted = cover.cost;
    var actual = composed + cover.bundles.reduce(function (s, sku) {
      var b = d.bundles.filter(function (x) { return x.sku === sku; })[0];
      return s + (b ? b.price : 0);
    }, 0);
    var drift = Math.round((actual - quoted) * 100) / 100;

    return { lines: lines, unresolved: unresolved, quoted: quoted, actual: actual, drift: drift };
  }

  /* ---------------------------------------------------------------------------------------
     VISIBLE MESSAGES. Before this existed EVERY refusal in this file was console.error only,
     and the React builder calls GXBundleCheckout without looking at what comes back. So a
     refused cart presented to the customer as a button that did nothing at all. That is the
     single behaviour Doug reported most this session, and it was never one bug: it was the
     absence of any way to say no.

     Any `return` below must be paired with a notify() or the dead button is back.

     Styles are inline on purpose. Dawn owns generic class names in this theme (`.field` cost a
     full day), so a scoped id plus inline style is the one thing that cannot be reached by a
     stylesheet we do not control.
     --------------------------------------------------------------------------------------- */
  var PHONE = '1-800-677-3574';
  var HOURS = 'Mon-Fri, 8am-6pm CST';

  /* Cart total at or above this hands off to the phone for Barn Pricing (Doug, 2026-08-06).
     Measured on what the customer would pay NOW, so a subscribe cart counts after its discount. */
  var BARN_MIN = 1500;

  function notify(title, body, tel) {
    var old = document.getElementById('gx-bx-notice');
    if (old) old.remove();

    var wrap = document.createElement('div');
    wrap.id = 'gx-bx-notice';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-label', title);
    wrap.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:rgba(18,18,18,.55);' +
      'display:flex;align-items:center;justify-content:center;padding:20px;';

    var card = document.createElement('div');
    card.style.cssText = 'background:#fff;border-radius:16px;max-width:420px;width:100%;padding:28px 26px;' +
      'font-family:Montserrat,system-ui,sans-serif;color:#1a1a1a;text-align:center;' +
      'box-shadow:0 18px 60px rgba(0,0,0,.25);';

    var h = document.createElement('div');
    h.style.cssText = 'font-size:19px;font-weight:800;line-height:1.25;margin-bottom:10px;';
    h.textContent = title;

    var p = document.createElement('p');
    p.style.cssText = 'font-size:14.5px;line-height:1.55;color:#4a4a48;margin:0 0 18px;';
    p.textContent = body;

    card.appendChild(h);
    card.appendChild(p);

    if (tel) {
      var a = document.createElement('a');
      a.href = 'tel:+1' + PHONE.replace(/\D/g, '');
      a.style.cssText = 'display:block;background:#FE7272;color:#fff;text-decoration:none;font-weight:800;' +
        'font-size:17px;padding:14px 18px;border-radius:999px;margin-bottom:8px;';
      a.textContent = 'Call ' + PHONE;
      var hrs = document.createElement('div');
      hrs.style.cssText = 'font-size:12.5px;color:#6b6b67;margin-bottom:16px;';
      hrs.textContent = HOURS;
      card.appendChild(a);
      card.appendChild(hrs);
    }

    var close = document.createElement('button');
    close.type = 'button';
    close.style.cssText = 'background:none;border:0;font:inherit;font-size:13.5px;color:#6b6b67;' +
      'text-decoration:underline;cursor:pointer;padding:6px;';
    close.textContent = 'Back to my order';
    close.addEventListener('click', function () { wrap.remove(); });
    card.appendChild(close);

    wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
    document.addEventListener('keydown', function esc(e) {
      if (e.key === 'Escape') { wrap.remove(); document.removeEventListener('keydown', esc); }
    });

    wrap.appendChild(card);
    document.body.appendChild(wrap);
    close.focus();
  }

  /* Anything we cannot sell as quoted. Deliberately does NOT show the customer the reason:
     "unresolved SKU" means nothing to them, and a phone call is the recovery either way. */
  function notifyProblem() {
    notify('We could not build that order',
      'Something in this combination could not be added. Our team can put the order through for you.',
      true);
  }

  /* Public entry point. bundles-app.js calls this in place of the old Checkout.html navigation. */
  window.GXBundleCheckout = function (cart, mode, cadence) {
    var built = buildLines(cart, mode, cadence || '90');

    if (built.error || !built.lines || !built.lines.length) {
      console.error('[gx-bundle-checkout] cannot build cart', built);
      notifyProblem();
      return;
    }
    if (built.unresolved.length) {
      /* A missing variant or selling plan means part of the cart cannot be sold as quoted.
         Refuse rather than send a customer to checkout with a wrong or unsubscribable cart. */
      console.error('[gx-bundle-checkout] unresolved SKUs, refusing to add:', built.unresolved);
      notifyProblem();
      return;
    }

    /* BARN PRICING HANDOFF. Doug 2026-08-06: "when the total price is $1,500 or more", and
       "let them know that the cart quantity qualifies for Barn Pricing".

       This is a SALES handoff, not an error. Barn, Dealer, Vet and Non-profit pricing is
       phone-only today, so an order this size bought at retail is one the customer overpaid for
       and one we would rather have a conversation about.

       It also happens to close the last pricing gap: the per-unit rounding on Osteo-MAX and
       Amino-MAX only exceeds tolerance past 24 and 27 units, which are $2,842 and $4,729 orders.
       Nothing reachable under $1,500 can drift, so the self-serve path is exact by construction.

       THRESHOLD IS ON WHAT THE CUSTOMER WOULD PAY NOW, so a subscribe cart is measured after the
       20% discount, matching the figure on screen. `built.actual` is the one-time total. */
    var payNow = mode === 'sub' ? built.actual * (1 - (data().sub || 0)) : built.actual;
    if (payNow >= BARN_MIN) {
      notify('This order qualifies for Barn Pricing',
        'Orders of $' + BARN_MIN.toLocaleString() + ' or more are priced below what the site can offer. ' +
        'Call and we will set you up at barn rates.',
        true);
      return;
    }
    /* TOLERANCE IS 5 CENTS, NOT 1, AND THAT IS DELIBERATE.

       Two products have a FRACTIONAL per-unit tier rate: Osteo-MAX is $175.1575 and Amino-MAX is
       $118.4525. A variant price has to be whole cents, so the add-on rounds to $175.16 / $118.45
       and the error accumulates with each add-on unit. At 11 units that is 2 cents against the
       engine's own quote. A 1-cent tolerance blocked those orders outright.

       This guard exists to catch REAL mispricing, which shows up in dollars (the pre-fix drifts
       were $11 to $45). Sub-nickel differences are arithmetic rounding on a fractional rate, not
       a pricing error, and refusing a sale over 2 cents is the wrong trade. Do not widen this
       further: at a dime it would start hiding genuine composition mistakes. */
    if (Math.abs(built.drift) > 0.05) {
      /* The composed line items cost MORE than the engine quoted, because a quantity had to be
         split into smaller tier SKUs than it was priced at. Refuse rather than send the customer
         to checkout above the price they were shown.

         HISTORY, so nobody re-opens this as a bug. On 2026-08-06 the only tier SKUs were 1, 2 and
         4 gallon, so quantities 3, 5, 6, 7, 9, 10 and 11 composed from smaller tiers, lost the
         half-tier discount, and were refused HERE. The refusal was correct but silent, logged only
         to the console, so pressing the button did nothing at all. That is what Doug reported.

         The refusal was briefly relaxed to let those orders through at the higher price, then
         reverted the same day once the real fix landed: eleven tier SKUs created at the exact
         prices the engine quotes (GX300/600, OM300/600, JF300/600, AM300/600, MC300/600, HM300),
         and `units` in snippets/gx-sku-map.liquid extended to '1,2,3,4,6'. Every quantity 1-12 now
         composes at exactly the quoted price and this branch should never fire.

         IF IT FIRES AGAIN it means a real mismatch: a new quantity with no tier SKU, a tier SKU
         missing from the `units` list, or a variant price edited away from the engine's curve.
         Fix the cause. Do not relax this guard again. */
      console.error('[gx-bundle-checkout] composed $' + built.actual.toFixed(2) + ' exceeds quoted $' +
        built.quoted.toFixed(2) + ' by $' + built.drift.toFixed(2) +
        ' (a quantity has no exact tier SKU); refusing to overcharge.', built);
      notifyProblem();
      return;
    }

    var m = map();
    fetch(m.addUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: built.lines })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status && res.status !== 200) throw new Error(res.description || 'add failed');
        window.location.href = m.checkoutUrl || '/checkout';
      })
      .catch(function (err) {
        console.error('[gx-bundle-checkout] add to cart failed', err);
        notifyProblem();
      });
  };

  /* Exposed for the acceptance check: lets a test compare composed line items against the
     engine's quote without performing an add. */
  window.GXBundlePreview = buildLines;
})();
